import { useState } from "react";
import { GlassCard } from "@/components/ui/glass-card";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, Sparkles, Calendar, Info } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import constructionBot from "@/assets/characters/construction-bot.png";
import spaceExplorer from "@/assets/characters/space-explorer.png";
import gardener from "@/assets/characters/gardener.png";
import giftGiver from "@/assets/characters/gift-giver.png";
import chef from "@/assets/characters/chef.png";
import artist from "@/assets/characters/artist.png";
import scientist from "@/assets/characters/scientist.png";
import wizard from "@/assets/characters/wizard.png";
import mechanic from "@/assets/characters/mechanic.png";
import timeTraveler from "@/assets/characters/time-traveler.png";
import growthIllustration from "@/assets/characters/growth.svg";

type CharacterTheme = "construction" | "space" | "growth" | "gift" | "chef" | "artist" | "scientist" | "wizard" | "mechanic" | "timeTraveler" | "organicGrowth";
type VariantSize = "large" | "medium" | "small";

const characters = {
  construction: {
    image: constructionBot,
    title: "Building Something Awesome",
    description: "We're hard at work constructing this feature for you.",
  },
  space: {
    image: spaceExplorer,
    title: "Launching Soon",
    description: "This feature is preparing for launch. Get ready for takeoff!",
  },
  growth: {
    image: gardener,
    title: "Growing Your Feature",
    description: "We're nurturing this feature to maturity. Good things take time!",
  },
  gift: {
    image: giftGiver,
    title: "Something Special Coming",
    description: "We've got a surprise in store for you. Stay tuned!",
  },
  chef: {
    image: chef,
    title: "Cooking Up Something Great",
    description: "This feature is in the kitchen, being prepared with care.",
  },
  artist: {
    image: artist,
    title: "Painting Your Experience",
    description: "We're adding the finishing touches to make it perfect.",
  },
  scientist: {
    image: scientist,
    title: "Experimenting with Innovation",
    description: "Our team is testing and perfecting this feature.",
  },
  wizard: {
    image: wizard,
    title: "Conjuring Magic",
    description: "Something extraordinary is being crafted for you.",
  },
  mechanic: {
    image: mechanic,
    title: "Fine-Tuning the Details",
    description: "We're ensuring everything runs smoothly.",
  },
  timeTraveler: {
    image: timeTraveler,
    title: "The Future Is Coming",
    description: "Time-traveling technology will be here before you know it.",
  },
  organicGrowth: {
    image: growthIllustration,
    title: "Organic Growth in Progress",
    description: "Watch your feature flourish and reach new heights.",
  },
};

export default function ComingSoonPreviewPage() {
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterTheme>("construction");
  const [showNotifyButton, setShowNotifyButton] = useState(true);
  const [showTimeline, setShowTimeline] = useState(true);
  const [showDescription, setShowDescription] = useState(true);

  const currentCharacter = characters[selectedCharacter];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-brand-main to-brand-auxiliary2 bg-clip-text text-transparent">
            Coming Soon Design Preview
          </h1>
          <p className="text-muted-foreground">
            Choose your favorite design and character theme
          </p>
        </div>

        {/* Controls */}
        <GlassCard className="p-6" hover={false}>
          <div className="space-y-6">
            <div>
              <Label className="text-base font-semibold mb-3 block">Character Theme</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(Object.keys(characters) as CharacterTheme[]).map((theme) => (
                  <button
                    key={theme}
                    onClick={() => setSelectedCharacter(theme)}
                    className={`relative rounded-xl p-4 transition-all border-2 ${
                      selectedCharacter === theme
                        ? "border-brand-main bg-brand-main/10 shadow-lg scale-105"
                        : "border-border bg-background/50 hover:border-brand-auxiliary2/50"
                    }`}
                  >
                    <img
                      src={characters[theme].image}
                      alt={theme}
                      className="w-20 h-20 mx-auto mb-2"
                    />
                    <p className="text-sm font-medium capitalize">{theme}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border">
                <Label htmlFor="notify">Show Notify Button</Label>
                <Switch
                  id="notify"
                  checked={showNotifyButton}
                  onCheckedChange={setShowNotifyButton}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border">
                <Label htmlFor="timeline">Show Timeline</Label>
                <Switch
                  id="timeline"
                  checked={showTimeline}
                  onCheckedChange={setShowTimeline}
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border">
                <Label htmlFor="description">Show Description</Label>
                <Switch
                  id="description"
                  checked={showDescription}
                  onCheckedChange={setShowDescription}
                />
              </div>
            </div>
          </div>
        </GlassCard>

        {/* Variants */}
        <div className="space-y-8">
          {/* Full Page Hero Variant */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold">Full Page Hero</h2>
              <span className="text-xs bg-brand-auxiliary2/20 text-brand-auxiliary2 px-2 py-1 rounded">
                Large (400x400px)
              </span>
            </div>
            <GlassCard className="p-12" hover={false}>
              <div className="flex flex-col items-center justify-center text-center space-y-6 max-w-2xl mx-auto">
                <div className="relative">
                  <img
                    src={currentCharacter.image}
                    alt={selectedCharacter}
                    className="w-96 h-96 object-contain"
                  />
                  <Sparkles className="absolute -top-4 -right-4 h-12 w-12 text-brand-auxiliary2 animate-pulse" />
                  <Sparkles className="absolute -bottom-2 -left-2 h-8 w-8 text-brand-main animate-pulse delay-300" />
                </div>

                <div className="space-y-3">
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-brand-main to-brand-auxiliary2 bg-clip-text text-transparent">
                    {currentCharacter.title}
                  </h3>
                  {showDescription && (
                    <p className="text-lg text-muted-foreground">
                      {currentCharacter.description}
                    </p>
                  )}
                </div>

                {(showNotifyButton || showTimeline) && (
                  <div className="flex flex-col sm:flex-row gap-4 items-center">
                    {showNotifyButton && (
                      <Button size="lg" className="gap-2">
                        <Bell className="h-4 w-4" />
                        Notify Me When Ready
                      </Button>
                    )}
                    {showTimeline && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">Expected: Q1 2025</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </GlassCard>
          </div>

          {/* Card-Based Inline Variant */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold">Card-Based Inline</h2>
              <span className="text-xs bg-brand-auxiliary2/20 text-brand-auxiliary2 px-2 py-1 rounded">
                Medium (200x200px)
              </span>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <GlassCard className="p-8" hover={false}>
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="relative">
                    <img
                      src={currentCharacter.image}
                      alt={selectedCharacter}
                      className="w-48 h-48 object-contain"
                    />
                    <Sparkles className="absolute -top-2 -right-2 h-8 w-8 text-brand-auxiliary2 animate-pulse" />
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-2xl font-bold text-foreground">
                      {currentCharacter.title}
                    </h4>
                    {showDescription && (
                      <p className="text-sm text-muted-foreground">
                        {currentCharacter.description}
                      </p>
                    )}
                  </div>

                  {showNotifyButton && (
                    <Button className="gap-2 w-full">
                      <Bell className="h-4 w-4" />
                      Notify Me
                    </Button>
                  )}

                  {showTimeline && (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Calendar className="h-3 w-3" />
                      <span>Expected: Q1 2025</span>
                    </div>
                  )}
                </div>
              </GlassCard>

              <div className="flex items-center justify-center p-8 border-2 border-dashed border-border rounded-2xl">
                <p className="text-muted-foreground text-center">
                  This variant can be embedded<br />within any page content
                </p>
              </div>
            </div>
          </div>

          {/* Banner Style Variant */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-semibold">Banner Style</h2>
              <span className="text-xs bg-brand-auxiliary2/20 text-brand-auxiliary2 px-2 py-1 rounded">
                Small (100x100px)
              </span>
            </div>
            <GlassCard className="p-6" hover={false}>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <img
                  src={currentCharacter.image}
                  alt={selectedCharacter}
                  className="w-24 h-24 object-contain flex-shrink-0"
                />

                <div className="flex-1 space-y-2 text-center sm:text-left">
                  <h4 className="text-xl font-bold text-foreground">
                    {currentCharacter.title}
                  </h4>
                  {showDescription && (
                    <p className="text-sm text-muted-foreground">
                      {currentCharacter.description}
                    </p>
                  )}
                  {showTimeline && (
                    <div className="flex items-center gap-2 text-muted-foreground text-xs justify-center sm:justify-start">
                      <Calendar className="h-3 w-3" />
                      <span>Expected: Q1 2025</span>
                    </div>
                  )}
                </div>

                {showNotifyButton && (
                  <Button className="gap-2 flex-shrink-0">
                    <Bell className="h-4 w-4" />
                    Notify Me
                  </Button>
                )}
              </div>
            </GlassCard>
          </div>
        </div>

        {/* Usage Examples */}
        <GlassCard className="p-6" hover={false}>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-brand-main" />
              <h3 className="text-xl font-semibold">Usage Recommendations</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="p-4 rounded-lg bg-background/50 border border-border space-y-2">
                <p className="font-semibold text-foreground">Full Page Hero</p>
                <p className="text-muted-foreground">
                  Best for: Dedicated landing pages, major features, standalone announcements
                </p>
              </div>
              <div className="p-4 rounded-lg bg-background/50 border border-border space-y-2">
                <p className="font-semibold text-foreground">Card-Based Inline</p>
                <p className="text-muted-foreground">
                  Best for: Feature sections, tab content, dashboard widgets
                </p>
              </div>
              <div className="p-4 rounded-lg bg-background/50 border border-border space-y-2">
                <p className="font-semibold text-foreground">Banner Style</p>
                <p className="text-muted-foreground">
                  Best for: Page headers, quick announcements, compact spaces
                </p>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
